import {
  EuiCallOut,
  EuiCheckbox,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import { ToolSpec } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { Fragment } from "react";
import { FieldTableName } from "../../fields/FieldTableName";
import { FieldTableKey } from "../../fields/FieldTableKey";
import { FieldParamTable } from "../../fields/FieldParamTable";
import { FieldParamMapping } from "../../fields/FieldParamMapping";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";

interface StepSummaryProps {
  toolSpec: ToolSpec;
  tableName: { value: string; isDirty: boolean };
  setTableName: (tableName: string, isDirty: boolean) => void;
  tableKey: string;
  setTableKey: (tableKey: string) => void;
  paramMap: {
    [paramKey: string]:
      | {
          tableId: string | undefined;
          columnId: string | undefined;
        }
      | undefined;
  };
  columnGroupName: { value: string; isDirty: boolean };
  setColumnGroupName: (columnGroupName: string, isDirty: boolean) => void;
  dataTableId: string | undefined;
  paramKeys: string[];
  autoPopulateRows: boolean;
  setAutoPopulateRows: (autoPopulateRows: boolean) => void;
}

export const StepSummary = ({
  toolSpec,
  tableName,
  setTableName,
  tableKey,
  setTableKey,
  columnGroupName,
  setColumnGroupName,
  dataTableId,
  paramMap,
  paramKeys,
  autoPopulateRows,
  setAutoPopulateRows,
}: StepSummaryProps) => {
  const allDataTableIds = useDataTableContext((s) => {
    return s.tables
      .filter((table) => table.kind === "data")
      .map((table) => table.id);
  });

  return (
    <Fragment>
      <EuiText>
        <h4>Summary</h4>
      </EuiText>

      <EuiSpacer size="s" />

      <EuiText size="s">
        <p>Please review your selections before creating the analysis table.</p>
      </EuiText>

      <EuiSpacer />

      <EuiFormRow fullWidth label="Analysis">
        <EuiFieldText
          fullWidth
          readOnly
          disabled
          icon="indexMapping"
          value={toolSpec.name}
        />
      </EuiFormRow>

      <EuiSpacer />

      <EuiFlexGroup>
        <EuiFlexItem>
          <FieldTableName
            label="Table name"
            value={tableName.value}
            onChange={({ value }) => {
              setTableName(value, true);
            }}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false} style={{ width: "200px" }}>
          <FieldTableKey
            tableName={tableName.value}
            label="Table key"
            value={tableKey}
            onChange={({ value }) => {
              setTableKey(value);
            }}
            expandRequirements={false}
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer />

      {dataTableId !== undefined && (
        <>
          <EuiCallOut>
            <EuiCheckbox
              id="auto-populate-rows"
              label={<b>Auto-populate rows</b>}
              checked={autoPopulateRows}
              onChange={() => setAutoPopulateRows(!autoPopulateRows)}
            />

            <EuiSpacer size="s" />

            <EuiText size="s">
              <p>
                Create a row in the new analysis table for every row in this
                data table.
              </p>
            </EuiText>
          </EuiCallOut>

          <EuiSpacer />
        </>
      )}

      <FieldParamMapping
        readOnly
        inputsLabel="Mapped analysis inputs"
        parametersLabel="Mapped analysis parameters"
        toolSpec={toolSpec}
        hideTableSelector={dataTableId !== undefined}
        tableIdOptions={
          dataTableId !== undefined ? [dataTableId] : allDataTableIds
        }
        isAlwaysExpanded
        hideEmptyParamsTable
        value={paramMap}
        onChange={() => undefined}
      />

      {dataTableId !== undefined && (
        <>
          <FieldTableName
            label="Column group name"
            placeholder=""
            value={columnGroupName.value}
            onChange={(field) => {
              setColumnGroupName(field.value, true);
            }}
          />

          <FieldParamTable
            resultsLabel="Joined analysis results"
            inputsLabel="Joined analysis inputs & parameters"
            isAlwaysExpanded
            hideEmptyParamsTable
            readOnly
            toolSpec={toolSpec}
            value={paramKeys}
            onChange={() => undefined}
          />
        </>
      )}
    </Fragment>
  );
};
